import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Users, Trophy, TrendingUp, Target, Award, Shield, Edit } from 'lucide-react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';

const TeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const teamsQuery = query(
      collection(db, 'teams'),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(teamsQuery, (snapshot) => {
      const teamsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTeams(teamsData);
      setLoading(false);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #F0FDF4 0%, #FFFFFF 50%, #DBEAFE 100%)' }}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}
        >
          <div>
            <h1 className="text-gradient" style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#1C563D' }}>
              Teams
            </h1>
            <p style={{ color: '#6B7280', fontSize: '1.125rem' }}>
              Manage teams and view statistics
            </p>
          </div>
          <Link
            to="/teams/create"
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Plus size={20} />
            Create Team
          </Link>
        </motion.div>

        {/* Stats Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}
        >
          <motion.div
            whileHover={{ scale: 1.03, y: -4 }}
            style={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              borderRadius: '1rem',
              padding: '1.5rem',
              color: 'white',
              boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)',
              textAlign: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <Shield size={40} style={{ opacity: 0.8 }} />
              <TrendingUp size={24} style={{ opacity: 0.6 }} />
            </div>
            <div style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>{teams.length}</div>
            <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', fontSize: '0.875rem' }}>Total Teams</div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.03, y: -4 }}
            style={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              borderRadius: '1rem',
              padding: '1.5rem',
              color: 'white',
              boxShadow: '0 10px 30px rgba(59, 130, 246, 0.3)',
              textAlign: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <Users size={40} style={{ opacity: 0.8 }} />
              <TrendingUp size={24} style={{ opacity: 0.6 }} />
            </div>
            <div style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              {teams.reduce((sum, t) => sum + (t.players?.length || 0), 0)}
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', fontSize: '0.875rem' }}>Total Players</div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.03, y: -4 }}
            style={{
              background: 'linear-gradient(135deg, #A855F7 0%, #9333EA 100%)',
              borderRadius: '1rem',
              padding: '1.5rem',
              color: 'white',
              boxShadow: '0 10px 30px rgba(168, 85, 247, 0.3)',
              textAlign: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <Trophy size={40} style={{ opacity: 0.8 }} />
              <TrendingUp size={24} style={{ opacity: 0.6 }} />
            </div>
            <div style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              {teams.reduce((sum, t) => sum + (t.wins || 0), 0)}
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', fontSize: '0.875rem' }}>Total Wins</div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.03, y: -4 }}
            style={{
              background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
              borderRadius: '1rem',
              padding: '1.5rem',
              color: 'white',
              boxShadow: '0 10px 30px rgba(245, 158, 11, 0.3)',
              textAlign: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <Target size={40} style={{ opacity: 0.8 }} />
              <TrendingUp size={24} style={{ opacity: 0.6 }} />
            </div>
            <div style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              {teams.reduce((sum, t) => sum + (t.matchesPlayed || 0), 0)}
        </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', fontSize: '0.875rem' }}>Matches Played</div>
          </motion.div>
        </motion.div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '5rem 0' }}>
            <div style={{ textAlign: 'center' }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                <Shield size={48} style={{ margin: '0 auto 1rem', color: '#10B981' }} />
              </motion.div>
              <p style={{ color: '#6B7280', fontWeight: '600' }}>Loading teams...</p>
            </div>
          </div>
        ) : teams.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
          {teams.map((team, index) => (
            <motion.div
              key={team.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.03, y: -6 }}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '1rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s'
                }}
              >
                {/* Team Header with Logo */}
                <div style={{
                  background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                  padding: '1.5rem',
                  position: 'relative'
                }}>
                  <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                    <Link
                      to={`/teams/${team.id}/edit`}
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(8px)',
                        padding: '0.5rem',
                        borderRadius: '0.5rem',
                        display: 'inline-block',
                        transition: 'all 0.3s'
                      }}
                    >
                      <Edit size={16} style={{ color: 'white' }} />
                    </Link>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      backgroundColor: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                      border: '4px solid rgba(255, 255, 255, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {team.logo ? (
                        <img
                          src={team.logo}
                          alt={`${team.name} logo`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Shield size={40} style={{ color: 'white' }} />
                    </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.25rem' }}>
                        {team.name}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                        <Users size={16} />
                        <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{team.players?.length || 0} Players</span>
                    </div>
                  </div>
                  </div>
                </div>

                {/* Team Stats */}
                <div style={{ padding: '1.25rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#059669' }}>
                        {team.matchesPlayed || 0}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#4B5563', fontWeight: '600' }}>Played</div>
                    </div>
                    
                    <div style={{
                      background: 'linear-gradient(135deg, #DBEAFE 0%, #93C5FD 100%)',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2563EB' }}>
                        {team.wins || 0}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#4B5563', fontWeight: '600' }}>Wins</div>
                    </div>
                    
                    <div style={{
                      background: 'linear-gradient(135deg, #E9D5FF 0%, #C084FC 100%)',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#9333EA' }}>
                        {team.points || 0}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#4B5563', fontWeight: '600' }}>Points</div>
                    </div>
                  </div>

                  {/* Win Rate */}
                  {team.matchesPlayed > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                        <span style={{ color: '#6B7280', fontWeight: '600' }}>Win Rate</span>
                        <span style={{ fontWeight: '700', color: '#10B981' }}>
                          {Math.round((team.wins / team.matchesPlayed) * 100)}%
                        </span>
                </div>
                      <div style={{ width: '100%', backgroundColor: '#E5E7EB', borderRadius: '9999px', height: '8px', overflow: 'hidden' }}>
                        <div
                          style={{
                            background: 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                            height: '100%',
                            borderRadius: '9999px',
                            width: `${(team.wins / team.matchesPlayed) * 100}%`,
                            transition: 'width 0.5s ease'
                          }}
                        ></div>
                </div>
          </div>
        )}

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Link
                      to={`/teams/${team.id}`}
                      className="btn btn-secondary"
                      style={{ flex: 1, textAlign: 'center', fontSize: '0.875rem', textDecoration: 'none' }}
                    >
                      View Team
                    </Link>
                    <Link
                      to={`/teams/${team.id}/edit`}
                      className="btn btn-primary"
                      style={{ flex: 1, textAlign: 'center', fontSize: '0.875rem', textDecoration: 'none' }}
                    >
                      Edit
                    </Link>
                </div>
                </div>
            </motion.div>
            ))}
          </div>
        ) : (
            <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            style={{ textAlign: 'center', padding: '5rem 0' }}
          >
            <div style={{ backgroundColor: 'white', borderRadius: '1rem', padding: '3rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', display: 'inline-block' }}>
              <Shield size={64} style={{ margin: '0 auto 1rem', color: '#9CA3AF' }} />
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#374151', marginBottom: '0.5rem' }}>
                No Teams Yet
              </h3>
              <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
                Create your first team to get started
              </p>
              <Link
                to="/teams/create"
                className="btn btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
              >
                <Plus size={20} />
                Create First Team
              </Link>
                </div>
            </motion.div>
        )}
      </div>
    </div>
  );
};

export default TeamsPage;
